import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
} from '@mui/material';
import { setViewContentPanel } from 'GeminiViewerComponent/features/contentPanel/contentPanelItemSlice';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { ChapterButtonsLocation, DisplayTypeKeys } from 'GeminiViewerComponent/helpers';
import { FormTextField } from 'components/TextField';
import { openPanel, selectFormInfo } from 'features/common/formSlice';
import {
    fetchContentById,
    fetchContentByTypeId,
    fetchContentVersions,
    getLastCreatedContent,
    resetContentStatus,
} from 'features/content/contentSlice';
import { Form, Formik } from 'formik';
import { CheckboxWithLabel } from 'formik-mui';
import { makeFormStyles } from 'forms/styles';
import { LinkTypes } from 'helpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdAdd, MdClear, MdDelete, MdLock, MdLockOpen } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { VideoChapterField } from '../../ContentViewActionNodeEditor/components/VideoChapterField';
import { SelectContentDialog } from './components/SelectContentDialog';
import { getPdfDimensionsInInches } from 'helpers/pdfHelpers';

const ContentWidthHeightField = ({ values, setFieldValue, hideAspectRatio = false, hideEndAdornment = false, isPdf=false}) => {
    const [lockAspectRatio, setLockAspectRatio] = useState(true);
    const [aspectRatio, setAspectRatio] = useState(null);

    const inputProps = {
        ...(hideEndAdornment
            ? {}
            : { endAdornment: <InputAdornment position="end">px</InputAdornment> }),
    };

    useEffect(() => {
        if (!values?.content_id || !values?.contentVersions) {
            setAspectRatio(null);
            return;
        }

        const version = values.selectedVersion
            ? values.contentVersions.find((v) => v.content_version_id === values.selectedVersion)
            : values.contentVersions[0];
        if (!version || !version.file_width || !version.file_height) {
            setAspectRatio(null);
            return;
        }

        setAspectRatio(version.file_width / version.file_height);
    }, [values?.content_id, values?.contentVersions, values?.selectedVersion]);

    useEffect(() => {
        if (!lockAspectRatio || !aspectRatio) return;

        if (values.width) {
            setFieldValue('height', Math.round(Number(values.width) / aspectRatio));
        } else if (values.height) {
            setFieldValue('width', Math.round(Number(values.height) * aspectRatio));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aspectRatio]);

    useEffect(() => {
        if (!isPdf) return;

        const pdfUrl = values?.content_url;
        if (!pdfUrl) return;

        // Only auto-load if both width and height are empty
        const hasWidth = values?.width !== null && values?.width !== '' && values?.width !== undefined;
        const hasHeight = values?.height !== null && values?.height !== '' && values?.height !== undefined;
        if (hasWidth || hasHeight) return;

        let cancelled = false;

        (async () => {
            try {
                const { widthInches, heightInches } =
                    await getPdfDimensionsInInches(pdfUrl);

                if (cancelled) return;

                setFieldValue('width', Number(widthInches.toFixed(1)));
                setFieldValue('height', Number(heightInches.toFixed(1)));
            } catch (e) {
                console.error(e);
            }
        })();

        return () => {
            cancelled = true;
        };

    }, [isPdf, values?.content_url]);

    return (
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <FormControl fullWidth>
                <FormTextField
                    label="Width"
                    type="number"
                    value={values.width ?? ''}
                    name="width"
                    placeholder="Width"
                    onChange={(evt) => {
                        setFieldValue('width', evt.target.value);
                        if (lockAspectRatio && aspectRatio) {
                            setFieldValue('height', Math.round(Number(evt.target.value) / aspectRatio));
                        }
                    }}
                    InputProps={inputProps}
                />
            </FormControl>
            <FormControl fullWidth>
                <FormTextField
                    label="Height"
                    type="number"
                    value={values.height ?? ''}
                    name="height"
                    placeholder="Height"
                    onChange={(evt) => {
                        setFieldValue('height', evt.target.value);
                        if (lockAspectRatio && aspectRatio) {
                            setFieldValue('width', Math.round(Number(evt.target.value) * aspectRatio));
                        }
                    }}
                    InputProps={inputProps}
                />
            </FormControl>
            {!hideAspectRatio && (
                <Tooltip
                    title={lockAspectRatio ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                    disableHoverListener={!aspectRatio}
                >
                    <span>
                        <IconButton
                            disabled={!aspectRatio}
                            onClick={() => setLockAspectRatio((v) => !v)}
                            tooltip="Lock aspect ratio"
                        >
                            {lockAspectRatio && aspectRatio ? <MdLock /> : <MdLockOpen />}
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Box>
    );
};

const ContentMaxWidthField = ({ values, setFieldValue}) => {
    return (
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <FormControl fullWidth>
                <FormTextField
                    label="Max Width"
                    type="number"
                    value={values.max_width ?? ''}
                    name="max_width"
                    placeholder="1600"
                    onChange={(evt) => {
                        setFieldValue('max_width', evt.target.value);
                    }}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">px</InputAdornment>,
                    }}
                />
            </FormControl>
        </Box>
    );
};

const ContentFormField = ({
    values,
    setFieldValue,
    selectedContent,
    setOpenContentDialog,
    setIsSelectingVideoPoster,
    handleCreateNewContent,
    isVideoPoster = false,
}) => {
    const dispatch = useDispatch();
    const theme = useSelector(selectDefaultTheme);
    const classes = makeFormStyles(theme);

    return (
        <>
            <Box display="flex" alignItems="center" gap={2}>
                <Autocomplete
                    className={classes.contentFormField}
                    fullWidth
                    variant="outline"
                    id={isVideoPoster ? 'poster' : 'content'}
                    name={isVideoPoster ? 'poster' : 'content'}
                    value={selectedContent}
                    readOnly
                    options={[]}
                    disableClearable
                    clearIcon={<MdDelete fontSize="18" />}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            margin="dense"
                            label={isVideoPoster ? 'Poster Image' : 'Content'}
                            placeholder={isVideoPoster ? 'Poster Image' : 'Content'}
                        />
                    )}
                    freeSolo
                    onClickCapture={() => {
                        setIsSelectingVideoPoster?.(isVideoPoster);
                        setOpenContentDialog(true);
                    }}
                />
                {!isVideoPoster && (
                    <Tooltip title="Add new content">
                        <IconButton className={classes.addNewButton} size="large" onClick={handleCreateNewContent}>
                            <MdAdd className="react-icon" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            {((isVideoPoster && values?.poster_image_name && values?.posterVersions?.length > 0) ||
                (!isVideoPoster && values?.contentVersions?.length > 0)) && (
                <FormControl
                    variant="outlined"
                    fullWidth
                    className={classes.handleClear}
                    style={{
                        position: 'relative',
                    }}
                >
                    <Autocomplete
                        variant="outline"
                        id={isVideoPoster ? 'posterVersion' : 'selectedVersion'}
                        name={isVideoPoster ? 'posterVersion' : 'selectedVersion'}
                        options={isVideoPoster ? values?.posterVersions : values?.contentVersions}
                        value={
                            isVideoPoster
                                ? values?.posterVersions?.find(
                                      (version) => version.content_version_id === +values?.posterVersion
                                  ) ?? null
                                : values?.contentVersions?.find(
                                      (version) => version.content_version_id === +values?.selectedVersion
                                  ) ?? null
                        }
                        clearIcon={<MdDelete fontSize="18" />}
                        onChange={async (_, newInputValue) => {
                            if (isVideoPoster) {
                                setFieldValue('poster_image_url', newInputValue?.url);
                                setFieldValue('posterVersion', newInputValue.content_version_id);
                            } else {
                                setFieldValue('selectedVersion', newInputValue.content_version_id);
                                setFieldValue('selectedChapter', null);
                                dispatch(activateLoading());
                                const versions = await dispatch(fetchContentVersions(values?.content_id));
                                const chapters = versions?.payload?.find(
                                    (version) => version.content_version_id === newInputValue.content_version_id
                                );
                                dispatch(deactivateLoading());
                                setFieldValue('chapters', chapters.chapters);
                            }
                        }}
                        disableClearable
                        getOptionLabel={(option) => `Version ${option?.version}`}
                        renderInput={(params) => (
                            <>
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    margin="dense"
                                    label={isVideoPoster ? 'Poster Version' : 'Version'}
                                    placeholder={isVideoPoster ? 'Poster Version' : 'Version'}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        right: '25px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        fontSize: '20px',
                                    }}
                                    className="clearSelect"
                                >
                                    <IconButton
                                        onClick={() => {
                                            setFieldValue('selectedVersion', null);
                                        }}
                                    >
                                        <MdClear />
                                    </IconButton>
                                </Box>
                            </>
                        )}
                    />
                </FormControl>
            )}
        </>
    );
};

const VideoContentForm = ({ values, setFieldValue, selectedPoster, ...props }) => {
    const theme = useSelector(selectDefaultTheme);
    const classes = makeFormStyles(theme);

    return (
        <>
            <FormControl sx={{ mr: 'auto' }}>
                <FormTextField
                    component={CheckboxWithLabel}
                    Label={{
                        label: 'Auto Play',
                    }}
                    label="Auto Play"
                    type="checkbox"
                    checked={values.auto_play}
                    onChange={(evt) => setFieldValue('auto_play', evt.target.checked)}
                />
            </FormControl>
            <FormControl sx={{ mr: 'auto' }}>
                <FormTextField
                    component={CheckboxWithLabel}
                    Label={{
                        label: 'Display Chapters',
                    }}
                    label="Display Chapters"
                    type="checkbox"
                    checked={values.show_chapters}
                    onChange={(evt) => {
                        if (!evt.target.checked) {
                            setFieldValue('collapse_chapters', false);
                        }
                        setFieldValue('show_chapters', evt.target.checked);
                    }}
                />
            </FormControl>
            {values.show_chapters && (
                <>
                    <FormControl fullWidth>
                        <FormTextField
                            component={CheckboxWithLabel}
                            Label={{
                                label: 'Collapse Chapters',
                            }}
                            label="Collapse Chapters"
                            type="checkbox"
                            checked={values.collapse_chapters}
                            onChange={(evt) => setFieldValue('collapse_chapters', evt.target.checked)}
                        />
                    </FormControl>
                    <Autocomplete
                        fullWidth
                        variant="outline"
                        id="chapter-buttons-location"
                        value={values?.chapter_buttons_location}
                        options={Object.values(ChapterButtonsLocation)}
                        clearIcon={<MdDelete fontSize="18" />}
                        onChange={(_, newInputValue) => {
                            setFieldValue('chapter_buttons_location', newInputValue);
                        }}
                        disableClearable
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                margin="dense"
                                variant="outlined"
                                label="Chapter Buttons Location"
                                placeholder="Chapter Buttons Location"
                            />
                        )}
                    />
                </>
            )}
            <ContentFormField values={values} setFieldValue={setFieldValue} {...props}></ContentFormField>
            {values?.chapters && values?.chapters?.length > 0 && (
                <FormControl
                    variant="outlined"
                    fullWidth
                    className={classes.handleClear}
                    style={{
                        position: 'relative',
                    }}
                >
                    <Autocomplete
                        variant="outline"
                        id="selectedChapter"
                        name="selectedChapter"
                        options={values?.chapters}
                        value={
                            values?.chapters?.find((chapter) => chapter.chapter_id === +values?.selectedChapter) ?? null
                        }
                        clearIcon={<MdDelete fontSize="18" />}
                        onChange={(_, newInputValue) => {
                            setFieldValue('selectedChapter', newInputValue.chapter_id);
                        }}
                        disableClearable
                        getOptionLabel={(option) => option?.title}
                        renderInput={(params) => (
                            <>
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    margin="dense"
                                    label="Chapter"
                                    placeholder="Chapter"
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        right: '25px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        fontSize: '20px',
                                    }}
                                    className={'clearSelect'}
                                >
                                    <IconButton
                                        onClick={() => {
                                            setFieldValue('selectedChapter', null);
                                        }}
                                    >
                                        <MdClear />
                                    </IconButton>
                                </Box>
                            </>
                        )}
                    />
                </FormControl>
            )}
            <ContentFormField
                values={values}
                setFieldValue={setFieldValue}
                isVideoPoster={true}
                {...props}
                selectedContent={values?.poster_image_name || selectedPoster}
            ></ContentFormField>
            <ContentWidthHeightField values={values} setFieldValue={setFieldValue} />
            <ContentMaxWidthField values={values} setFieldValue={setFieldValue} />            
        </>
    );
};

const ImageContentForm = ({ values, setFieldValue, ...props }) => {
    return (
        <>
            <ContentFormField values={values} setFieldValue={setFieldValue} {...props}></ContentFormField>
            <FormControl fullWidth>
                <FormTextField
                    label="Description"
                    value={values.altText ?? ''}
                    name="altText"
                    placeholder="Description"
                    onChange={(evt) => setFieldValue('altText', evt.target.value)}
                />
            </FormControl>
            <ContentWidthHeightField values={values} setFieldValue={setFieldValue} />
            <ContentMaxWidthField values={values} setFieldValue={setFieldValue} />
        </>
    );
};

const PdfContentForm = ({ values, setFieldValue, ...props }) => {
    return (
        <>
            <ContentFormField values={values} setFieldValue={setFieldValue} {...props}></ContentFormField>
            <FormControl fullWidth>
                <FormTextField
                    label="Description"
                    value={values.title ?? ''}
                    name="title"
                    placeholder="Description"
                    onChange={(evt) => setFieldValue('title', evt.target.value)}
                />
            </FormControl>
            <FormControl fullWidth>
                <FormTextField
                    label="Page Number"
                    value={values.pageNumber ?? ''}
                    name="pageNumber"
                    placeholder="Page Number"
                    type="number"
                    inputProps={{ min: 1 }}
                    onChange={(evt) => setFieldValue('pageNumber', evt.target.value)}
                />
            </FormControl>
            <FormControl fullWidth sx={{ my: 1 }}>
                <InputLabel id="zoom-label">Zoom</InputLabel>
                <Select
                    labelId="zoom-label"
                    label="Zoom"
                    value={values.zoom ?? 'page-fit'}
                    name="zoom"
                    onChange={(evt) => setFieldValue('zoom', evt.target.value)}
                >
                    <MenuItem value="100">100</MenuItem>
                    <MenuItem value="page-fit">Fit Page</MenuItem> 
                    <MenuItem value="auto">Auto</MenuItem>
                </Select>
            </FormControl>
            <FormControl sx={{ mr: 'auto' }}>
                <FormTextField
                    component={CheckboxWithLabel}
                    Label={{
                        label: 'Hide PDF Toolbar',
                    }}
                    label="Hide PDF Toolbar"
                    type="checkbox"
                    checked={values.hideToolbar ?? false}
                    onChange={(evt) => setFieldValue('hideToolbar', evt.target.checked)}
                />
            </FormControl>
            <FormControl sx={{ mr: 'auto' }}>
                <FormTextField
                    component={CheckboxWithLabel}
                    Label={{
                        label: 'Display PDF Fullscreen Button',
                    }}
                    label="Display PDF Fullscreen Button"
                    type="checkbox"
                    checked={values.fullscreenmode ?? false}
                    onChange={(evt) => {
                        const checked = evt.target.checked;

                        setFieldValue('fullscreenmode', checked);

                        if (
                            checked &&
                            (values.btn_fullscreen_opacity === null ||
                                values.btn_fullscreen_opacity === undefined ||
                                values.btn_fullscreen_opacity === '')
                        ) {
                            setFieldValue('btn_fullscreen_opacity', 80);
                        }
                    }}
                />
            </FormControl>
            <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <FormControl fullWidth>
                    <FormTextField
                        label="Set button opacity (100% visible - 0% hidden)"
                        value={values.btn_fullscreen_opacity ?? ''}
                        name="btn_fullscreen_opacity"
                        placeholder="80"
                        type="number"
                        inputProps={{ min: 0, max: 100 }}
                        onChange={(evt) => setFieldValue('btn_fullscreen_opacity', evt.target.value)}
                    />
                </FormControl>
                <FormControl fullWidth sx={{ my: 1 }}>
                    <InputLabel id="btn-fullscreen-size-label">Set button size</InputLabel>
                    <Select
                        labelId="btn-fullscreen-size-label"
                        label="Set button size"
                        value={values.btn_fullscreen_size ?? 'normal'}
                        name="btn-fullscreen-size"
                        onChange={(evt) => setFieldValue('btn_fullscreen_size', evt.target.value)}
                    >
                        <MenuItem value="small">Small</MenuItem>
                        <MenuItem value="normal">Normal</MenuItem> 
                        <MenuItem value="large">Large</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <ContentWidthHeightField values={values} setFieldValue={setFieldValue} hideAspectRatio={false} hideEndAdornment isPdf />
            <ContentMaxWidthField values={values} setFieldValue={setFieldValue} />            
        </>
    );
};

const LinkContentForm = ({ values, setFieldValue, allowedDisplayTypes, ...props }) => {
    const displayModeMenuTypes = {
        [DisplayTypeKeys.Inline]: {
            value: 'inline',
            label: 'Inline',
        },
        [DisplayTypeKeys.External]: {
            value: 'external',
            label: 'External',
        },
        [DisplayTypeKeys.CompanionApp]: {
            value: 'companion_app',
            label: 'Companion App',
        },
    };

    return (
        <>
            <FormControl fullWidth>
                <FormTextField
                    label="Title"
                    value={values.link_text}
                    name="link_text"
                    placeholder="Title"
                    onChange={(evt) => setFieldValue('link_text', evt.target.value)}
                />
            </FormControl>
            <FormControl fullWidth>
                <FormTextField
                    component={CheckboxWithLabel}
                    Label={{
                        label: 'Display as a button',
                    }}
                    label="Display as a button"
                    type="checkbox"
                    checked={values.is_button}
                    onChange={(evt) => setFieldValue('is_button', evt.target.checked)}
                />
            </FormControl>
            <FormControl fullWidth>
                <FormTextField
                    component={CheckboxWithLabel}
                    Label={{
                        label: 'Auto play videos',
                    }}
                    label="Auto play videos"
                    type="checkbox"
                    checked={values.auto_play}
                    onChange={(evt) => setFieldValue('auto_play', evt.target.checked)}
                />
            </FormControl>
            <FormControl fullWidth sx={{ my: 1 }}>
                <InputLabel id="display_mode_label">Display Mode</InputLabel>
                <Select
                    labelId="display_mode"
                    id="display_mode"
                    value={values.display_mode}
                    label="Display Mode"
                    onChange={(e) => {
                        setFieldValue('display_mode', e.target.value);
                    }}
                >
                    {allowedDisplayTypes.map((mode) => {
                        const displayMode = displayModeMenuTypes[mode];
                        return (
                            <MenuItem key={displayMode.value} value={displayMode.value}>
                                {displayMode.label}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
            <ContentFormField values={values} setFieldValue={setFieldValue} {...props}></ContentFormField>
            <VideoChapterField />
        </>
    );
};

const AudioContentForm = ({ values, setFieldValue, ...props }) => {
    return (
        <>
            <ContentFormField values={values} setFieldValue={setFieldValue} {...props}></ContentFormField>
            <FormControl fullWidth>
                <FormTextField
                    label="Description"
                    value={values.altText ?? ''}
                    name="altText"
                    placeholder="Description"
                    onChange={(evt) => setFieldValue('altText', evt.target.value)}
                />
            </FormControl>
        </>
    );
};


const LearningItemContentForm = ({ values, setFieldValue, ...props }) => {
    return (
        <>
            <ContentFormField values={values} setFieldValue={setFieldValue} {...props}></ContentFormField>
            <ContentWidthHeightField values={values} setFieldValue={setFieldValue} hideAspectRatio />
            <ContentMaxWidthField values={values} setFieldValue={setFieldValue} />
        </>
    );
};

const EXISTING_CONTENT_INIT_VALUES = {
    display_mode: '',
    reference_mode: '',
    auto_play: true,
    link_text: '',
    is_button: false,
    content_id: null,
    max_width: null,    
};
const INLINE_VIDEO_INIT_VALUES = {
    auto_play: false,
    show_chapters: false,
    collapse_chapters: false,
    chapter_buttons_location: ChapterButtonsLocation[1],
    content_id: null,
    width: null,
    height: null,
    max_width: null,
};

const linkContentValidation = Yup.object({
    link_text: Yup.string().required('Required'),
});

const SelectContentForm = ({
    content,
    onSelectedContent,
    contentType, // link | image | video | pdf
    allowedDisplayTypes,
}) => {
    const dispatch = useDispatch();
    const isComponentLoaded = useRef(true);
    const [selectedContent, setSelectedContent] = useState(null);
    const [openContentDialog, setOpenContentDialog] = useState(false);
    const [selectedPoster, setSelectedPoster] = useState(null);
    const [isSelectingVideoPoster, setIsSelectingVideoPoster] = useState(false);
    const [sortBy, setSortBy] = useState('display_name');
    const [sortDirection, setSortDirection] = useState('ASC');
    const [contentPayload, setContentPayload] = useState({});
    const [hidden, setHidden] = useState(false);
    const formInfo = useSelector(selectFormInfo);
    const lastCreatedContent = useSelector(getLastCreatedContent);
    const lastCreatedContentId = useRef(null);
    const formikRef = useRef(null); // imperative ref to push new content_id into Formik without waiting for enableReinitialize

    let onEditValues = Object.create(null);
    if (content) {
        onEditValues['content_id'] = content.content_id;
        onEditValues['selectedVersion'] = content.selectedVersion;
        switch (contentType) {
            case 'video':
                onEditValues['auto_play'] = !!content.auto_play;
                onEditValues['show_chapters'] = !!content.show_chapters;
                onEditValues['collapse_chapters'] = !!content.collapse_chapters;
                onEditValues['chapter_buttons_location'] = content.chapter_buttons_location;
                onEditValues['poster_id'] = content.poster_id;
                onEditValues['selectedChapter'] = content.selectedChapter;
                onEditValues['posterVersion'] = content.posterVersion;
                onEditValues['poster_image_name'] = content.poster_image_name;
                onEditValues['poster_image_url'] = content.poster_image_url;
                onEditValues['width'] = content.width;
                onEditValues['height'] = content.height;
                onEditValues['max_width'] = content.max_width;                
                break;
            case 'image':
                onEditValues['altText'] = content.altText;
                onEditValues['width'] = content.width;
                onEditValues['height'] = content.height;
                onEditValues['max_width'] = content.max_width;
                break;
            case 'pdf':
                onEditValues['title'] = content.title;
                onEditValues['pageNumber'] = content.pageNumber;
                onEditValues['zoom'] = content.zoom;
                onEditValues['hideToolbar'] = content.hideToolbar;
                onEditValues['fullscreenmode'] = content.fullscreenmode;
                onEditValues['btn_fullscreen_opacity'] = content.btn_fullscreen_opacity;
                onEditValues['btn_fullscreen_size'] = content.btn_fullscreen_size;
                //onEditValues['width'] = content.width;
                //onEditValues['height'] = content.height;
                onEditValues['width'] = content.aspect_ratio_width;
                onEditValues['height'] = content.aspect_ratio_height;
                onEditValues['max_width'] = content.max_width;                
                break;
            case 'link':
                onEditValues['display_mode'] = content.display_mode;
                onEditValues['reference_mode'] = content.reference_mode;
                onEditValues['link_text'] = content.link_text;
                onEditValues['is_button'] = !!content.is_button;
                onEditValues['auto_play'] = !!content.auto_play;
                onEditValues['chapter_id'] = content.chapter_id;
                break;
            case 'audio':
                onEditValues['altText'] = content.altText;
                break;
            case 'learning':
                onEditValues['max_width'] = content.max_width;
                onEditValues['width'] = content.width;
                onEditValues['height'] = content.height;
                break;
        }
    } else {
        if (contentType === 'video') {
            onEditValues = {
                ...INLINE_VIDEO_INIT_VALUES,
            };
        } else {
            onEditValues = {
                ...EXISTING_CONTENT_INIT_VALUES,
            };
        }
    }

    const [initValues, setInitValues] = useState({ ...onEditValues });

    const getContentById = useCallback(async () => {
        await dispatch(activateLoading());
        const contentName = await dispatch(fetchContentById({ contentId: content?.content_id }));
        setSelectedContent(contentName?.payload?.display_name);
        const versions = await dispatch(fetchContentVersions(content?.content_id));
        const posterVersions = content?.poster_id ? await dispatch(fetchContentVersions(content?.poster_id)) : null;
        const contentChapters = versions?.payload?.find(
            (version) => version.content_version_id === initValues?.selectedVersion
        );
        if (Object.keys(contentChapters || {}).length > 0) {
            setInitValues({
                ...initValues,
                contentVersions: versions?.payload,
                posterVersions: posterVersions?.payload,
                chapters: contentChapters?.chapters,
            });
        } else {
            setInitValues({
                ...initValues,
                contentVersions: versions?.payload,
                posterVersions: posterVersions?.payload,
                chapters: contentName?.payload?.last_content_version?.chapters,
            });
        }
        await dispatch(deactivateLoading());
    }, [content?.content_id, content?.poster_id, dispatch, initValues]);

    const getContentData = async (payload) => {
        await dispatch(activateLoading());
        await dispatch(
            fetchContentByTypeId({
                ...payload,
                sort: `${sortDirection === 'ASC' ? '' : '-'}${
                    sortBy === 'content_type_id' ? 'content_type_name' : sortBy
                }`,
            })
        );
        await dispatch(deactivateLoading());
    };

    useEffect(() => {
        dispatch(setViewContentPanel(false));
        dispatch(resetContentStatus());
        if (isComponentLoaded.current) {
            isComponentLoaded.current = false;
            return;
        }
        getContentData(contentPayload);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentPayload, dispatch]);

    useEffect(() => {
        if (content) {
            getContentById();
            if (contentType === 'video') {
                setInitValues({
                    ...INLINE_VIDEO_INIT_VALUES,
                    ...onEditValues,
                });
            } else {
                setInitValues({
                    ...EXISTING_CONTENT_INIT_VALUES,
                    ...onEditValues,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, contentType]);

    const setSorting = (_sortBy, _sortDirection) => {
        setSortBy(_sortBy);
        setSortDirection(_sortDirection);
        if (_sortBy !== '') {
            setContentPayload({
                ...contentPayload,
                reset: true,
                startIndex: 0,
                stopIndex: 19,
                sort: `${_sortDirection === 'ASC' ? '' : '-'}${
                    _sortBy === 'content_type_id' ? 'content_type_name' : _sortBy
                }`,
            });
        }
    };

    const contentDialogProps = (setFieldValue, values) => {
        const props = {
            openDialog: openContentDialog,
            setOpenDialog: setOpenContentDialog,
            setFieldValue,
            setSorting,
            contentPayload,
            setContentPayload,
            sortBy,
            sortDirection,
            setSelectedContent,
            contentType,
        };
        if (isSelectingVideoPoster) {
            return {
                ...props,
                setSelectedPoster: setSelectedPoster,
            };
        }
        return props;
    };

    const handleCreateNewContent = (values) => {
        lastCreatedContentId.current = lastCreatedContent?.content_id;
        setInitValues((v) => ({ ...v, ...values }));
        setHidden(true);
        // Pass editObject directly to openPanel so linkTypeId (and other props) are
        // available on ContentForm's very first render. openPanel previously reset
        // editObjectInfo to null before setEditObject could fire, causing the Link Type
        // dropdown to flash visible or default to empty for the learning context.
        dispatch(
            openPanel({
                formKey: 'contentForm',
                formAction: 'Create',
                editObject: {
                    tabView: true,
                    linkTypeId:
                        contentType === 'video'
                            ? LinkTypes.Video.id
                            : contentType === 'image'
                            ? LinkTypes.Image.id
                            : contentType === 'pdf'
                            ? LinkTypes.Document.id
                            : contentType === 'audio'
                            ? LinkTypes.Audio.id
                            : contentType === 'learning'
                            ? LinkTypes.Learning.id
                            : null,
                    navigateToLinkForm: true,
                    openedFromModal: true,
                },
            })
        );
    };

    useEffect(() => {
        if (!formInfo.contentForm?.isOpen && hidden) {
            setHidden(false);
            if (lastCreatedContent && lastCreatedContent.content_id !== lastCreatedContentId.current) {
                setSelectedContent(lastCreatedContent.display_name);

                const newValues = {
                    contentVersions: [lastCreatedContent.last_content_version],
                    content_id: lastCreatedContent.content_id,
                    chapters:
                        contentType === 'video' && lastCreatedContent.chapters?.length > 0
                            ? lastCreatedContent.chapters
                            : null,
                };

                setInitValues((v) => ({ ...v, ...newValues }));

                // Also push directly into live Formik — enableReinitialize runs async and
                // content_id would be null if Save is clicked before the next tick
                if (formikRef.current) {
                    Object.entries(newValues).forEach(([key, val]) => {
                        formikRef.current.setFieldValue(key, val);
                    });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formInfo.contentForm?.isOpen, lastCreatedContent]);

    return (
        <Dialog open={!hidden} fullWidth maxWidth="sm">
            <DialogTitle>
                {contentType === 'video'
                    ? 'Select Inline Video'
                    : contentType === 'image'
                    ? 'Select Inline Image'
                    : contentType === 'pdf'
                    ? 'Select Inline PDF'
                    : contentType === 'learning'
                    ? 'Select Learning Item / Activity'
                    : 'Select Content'}
            </DialogTitle>
            <DialogContent
                style={{
                    paddingTop: '5px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Formik
                            innerRef={formikRef}
                            initialValues={initValues}
                            enableReinitialize={true}
                            validationSchema={contentType === 'link' && linkContentValidation}
                            validate={(values) => {
                                const errors = {};
                                if (
                                    contentType === 'learning' &&
                                    !values.content_id
                                ) {
                                    errors.content_id = 'Please select Learning Item Content.';
                                }
                                return errors;
                            }}
                            onSubmit={(values) => onSelectedContent(values, content)}
                        >
                            {({ setFieldValue, values, errors, touched, submitCount }) => (
                                <Form style={{ display: 'flex', flexDirection: 'column' }}>
                                    {contentType === 'video' ? (
                                        <VideoContentForm
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            selectedContent={selectedContent}
                                            setOpenContentDialog={setOpenContentDialog}
                                            selectedPoster={selectedPoster}
                                            setIsSelectingVideoPoster={setIsSelectingVideoPoster}
                                            handleCreateNewContent={() => handleCreateNewContent(values)}
                                        />
                                    ) : contentType === 'image' ? (
                                        <ImageContentForm
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            selectedContent={selectedContent}
                                            setOpenContentDialog={setOpenContentDialog}
                                            handleCreateNewContent={() => handleCreateNewContent(values)}
                                        />
                                    ) : contentType === 'pdf' ? (
                                        <PdfContentForm
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            selectedContent={selectedContent}
                                            setOpenContentDialog={setOpenContentDialog}
                                            handleCreateNewContent={() => handleCreateNewContent(values)}
                                        />
                                    ) : contentType === 'audio' ? (
                                        <AudioContentForm
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            selectedContent={selectedContent}
                                            setOpenContentDialog={setOpenContentDialog}
                                            handleCreateNewContent={() => handleCreateNewContent(values)}
                                        />
                                    ) : contentType === 'learning' ? (
                                        <LearningItemContentForm
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            selectedContent={selectedContent}
                                            setOpenContentDialog={setOpenContentDialog}
                                            handleCreateNewContent={() => handleCreateNewContent(values)}
                                        />
                                    ) : (
                                        <LinkContentForm
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            selectedContent={selectedContent}
                                            setOpenContentDialog={setOpenContentDialog}
                                            handleCreateNewContent={() => handleCreateNewContent(values)}
                                            allowedDisplayTypes={allowedDisplayTypes}
                                        />
                                    )}
                                    {contentType === 'learning' && submitCount > 0 && !values.content_id && (
                                        <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px' }}>
                                            {'Please select Learning Item Content.'}
                                        </div>
                                    )}
                                    <div
                                        style={{
                                            justifyContent: 'end',
                                            display: 'flex',
                                            marginTop: '25px',
                                        }}
                                    >
                                        <Button onClick={() => onSelectedContent(null)}>Cancel</Button>
                                        <Button type="submit">Save</Button>
                                    </div>
                                    {openContentDialog && (
                                        <SelectContentDialog {...contentDialogProps(setFieldValue, values)} />
                                    )}
                                </Form>
                            )}
                        </Formik>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default SelectContentForm;
